// Global Instructions Rule Applied!

import { createSelector } from '@reduxjs/toolkit'
import { InteractionsState } from '../../store_models/interactions.ts'
import { InteractionRecordFields } from '../../data_models/interaction_record.ts'
import { InteractionRecord } from '../../data_models/interaction_record.ts'

// Empty array constant to ensure stable reference
const EMPTY_INTERACTIONS_ARRAY: InteractionRecord[] = []

// Base selector to get interactions state
const selectInteractionsState = (state: { interactions: InteractionsState }) => state.interactions

// Factory function to create a memoized selector for a specific entityId
// This ensures we return the same array reference when the data hasn't changed
const makeSelectInteractionsByEntityId = () => {
  return createSelector([selectInteractionsState], (interactions): Record<string, InteractionRecord[]> => {
    return interactions || {}
  })
}

// Cache of selectors by entityId to avoid recreating them
const selectorCache = new Map<string, ReturnType<typeof makeSelectInteractionsByEntityId>>()

// Main selector function - uses cached memoized selector per entityId
export const selectInteractionsByEntityId = (
  state: { interactions: InteractionsState },
  entityId: string
): InteractionRecord[] => {
  if (!state.interactions || !entityId) {
    return EMPTY_INTERACTIONS_ARRAY
  }

  const entityInteractions = state.interactions[entityId]
  if (!entityInteractions || entityInteractions.length === 0) {
    return EMPTY_INTERACTIONS_ARRAY
  }

  // Return the actual array from state - this should be stable unless interactions actually change
  return entityInteractions
}

const selectInteractionById = (
  state: { interactions: InteractionsState },
  entityId: string,
  interactionId: string
): InteractionRecord | undefined => {
  const interactions = selectInteractionsByEntityId(state, entityId)
  const idField = InteractionRecordFields.interactions__id

  return interactions.find((interaction) => interaction[idField] === interactionId)
}
